import React, { useState, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';
import '../styles/Map.css';
import config from '../constants/config';
import { 
  FaChevronDown, 
  FaEnvelope, 
  FaGlobe, 
  FaFacebook, 
  FaTwitter, 
  FaLinkedin, 
  FaInstagram,
  FaPhone
} from 'react-icons/fa';

const Marker = ({ label, flag, onClick }) => {
  return (
    <div onClick={onClick} style={{ textAlign: "center", cursor: "pointer"}}>
      {flag && (
        <img
          src={flag}
          alt="Flag"
          style={{
            width: "40px",
            height: "25px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
          }}
        />
      )}
    </div>
  );
};

const FixedMarker = (props) => <Marker {...props} />;

const MembersMap = ({ countries }) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedMembers, setExpandedMembers] = useState({});

  const defaultProps = {
    center: {
      lat: 20.818191, 
      lng: -66.519294
    },
    zoom: 4.8
  };

  // Coordinate validation function
  const isValidCoordinate = useCallback((lat, lng) => {
    return (
      typeof lat === 'number' && 
      typeof lng === 'number' && 
      !isNaN(lat) && 
      !isNaN(lng) && 
      lat >= -90 && lat <= 90 && 
      lng >= -180 && lng <= 180
    );
  }, []);

  // Filter out invalid coordinates
  const validCountries = countries.filter(country => 
    isValidCoordinate(country.location.lat, country.location.lng)
  );

  const handleMarkerClick = (country) => {
    setSelectedCountry(country);
    setIsModalOpen(true);
    // Reset expanded members state when opening a new country
    setExpandedMembers({});
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCountry(null);
    setExpandedMembers({});
  };

  // Toggle member dropdown
  const toggleMemberDetails = (memberId) => {
    setExpandedMembers(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  return (
    <div style={{ height: '800px', width: '100%', position: 'relative' }}>
        <GoogleMapReact
        bootstrapURLKeys={{ key: config.google.api }}
        defaultCenter={defaultProps.center}
        defaultZoom={defaultProps.zoom}
        options={{
            draggable: false,        // Disable panning
            scrollwheel: false,      // Disable zooming with scroll
            disableDoubleClickZoom: true, // Disable zooming on double click
            gestureHandling: "none", // Disable all gestures
            zoomControl: false       // Remove zoom controls
        }}
        >
            {validCountries.map((country, index) => (
                <Marker
                key={index}
                lat={country.location.lat}  // ✅ This must be exactly here!
                lng={country.location.lng}  // ✅ This must be exactly here!
                label={country.country}
                flag={country.image}
                onClick={() => handleMarkerClick(country)}
                />
            ))}
        </GoogleMapReact>

      {/* Modal with dropdown functionality */}
      {isModalOpen && selectedCountry && (
        <div className="modal-overlay" style={modalOverlayStyle} onClick={handleCloseModal}>
          <div 
            className="modal-content" 
            style={modalContentStyle} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={modalHeaderStyle}>
              <h2>Members in {selectedCountry.country}</h2>
              <button 
                onClick={handleCloseModal} 
                style={closeButtonStyle}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={modalBodyStyle}>
              {selectedCountry.members && selectedCountry.members.length > 0 ? (
                <ul style={memberListStyle}>
                  {selectedCountry.members.map((member, index) => (
                    <li key={member.id || index}>
                      <div 
                        style={memberItemStyle} 
                        onClick={() => toggleMemberDetails(member.id || index)}
                      >
                        {/* Member row with image, name and chevron */}
                        {member.image && (
                          <img 
                            src={member.image} 
                            alt={member.name} 
                            style={memberImageStyle}
                          />
                        )}
                        <div style={memberNameContainerStyle}>
                          <p style={memberNameStyle}>{member.name}</p>
                        </div>
                        <div style={chevronContainerStyle}>
                          <span style={{
                            ...chevronStyle,
                            transform: expandedMembers[member.id || index] ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}>
                            <FaChevronDown size={14} />
                          </span>
                        </div>
                      </div>
                      
                      {/* Dropdown content */}
                      {expandedMembers[member.id || index] && (
                        <div style={dropdownContentStyle}>
                          {/* Email - only show if exists and not empty */}
                          {member.email && member.email.trim() !== '' && (
                            <div style={contactItemStyle}>
                              <FaEnvelope style={contactIconStyle} />
                              <a href={`mailto:${member.email}`} style={contactLinkStyle}>
                                {member.email}
                              </a>
                            </div>
                          )}
                          
                          {/* Phone - only show if exists and not empty */}
                          {member.phone && member.phone.trim() !== '' && (
                            <div style={contactItemStyle}>
                              <FaPhone style={contactIconStyle} />
                              <a href={`tel:${member.phone}`} style={contactLinkStyle}>
                                {member.phone}
                              </a>
                            </div>
                          )}
                          
                          {/* Website - only show if exists and not empty */}
                          {member.website && member.website.trim() !== '' && (
                            <div style={contactItemStyle}>
                              <FaGlobe style={contactIconStyle} />
                              <a href={member.website} target="_blank" rel="noopener noreferrer" style={contactLinkStyle}>
                                {member.website}
                              </a>
                            </div>
                          )}
                          
                          {/* Social Media Icons - only show if at least one exists */}
                          {((member.facebook_link && member.facebook_link.trim() !== '') ||
                            (member.linkedin_link && member.linkedin_link.trim() !== '') ||
                            (member.instagram_link && member.instagram_link.trim() !== '')) && (
                            <div style={socialMediaContainerStyle}>
                              {member.facebook_link && member.facebook_link.trim() !== '' && (
                                <a href={member.facebook_link} target="_blank" rel="noopener noreferrer" style={socialIconLinkStyle}>
                                  <FaFacebook style={socialIconStyle} />
                                </a>
                              )}
                              {member.linkedin_link && member.linkedin_link.trim() !== '' && (
                                <a href={member.linkedin_link} target="_blank" rel="noopener noreferrer" style={socialIconLinkStyle}>
                                  <FaLinkedin style={socialIconStyle} />
                                </a>
                              )}
                              {member.instagram_link && member.instagram_link.trim() !== '' && (
                                <a href={member.instagram_link} target="_blank" rel="noopener noreferrer" style={socialIconLinkStyle}>
                                  <FaInstagram style={socialIconStyle} />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={noMembersStyle}>No members found for this country.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Inline styles for modal
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '400px',
  maxHeight: '500px',
  overflow: 'auto',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '15px',
  borderBottom: '1px solid #e0e0e0'
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  color: '#666'
};

const modalBodyStyle = {
  padding: '15px'
};

const memberListStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0
};

const memberItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: '1px solid #f0f0f0',
  cursor: 'pointer'
};

const memberNameContainerStyle = {
  flex: 1
};

const memberImageStyle = {
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  objectFit: 'cover',
  marginRight: '15px'
};

const memberNameStyle = {
  margin: 0,
  fontWeight: 'bold'
};

const chevronContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  marginLeft: '10px'
};

const chevronStyle = {
  fontSize: '14px',
  color: '#666',
  transition: 'transform 0.3s ease'
};

const dropdownContentStyle = {
  padding: '10px 0', // Add padding to align with member name
  borderBottom: '1px solid #f0f0f0'
};

const contactItemStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '10px'
};

const contactIconStyle = {
  marginRight: '10px',
  color: '#555',
  fontSize: '14px'
};

const contactLinkStyle = {
  color: '#0078d4',
  textDecoration: 'none',
  fontSize: '14px'
};

const socialMediaContainerStyle = {
  display: 'flex',
  marginTop: '15px'
};

const socialIconLinkStyle = {
  marginRight: '15px',
  textDecoration: 'none'
};

const socialIconStyle = {
  fontSize: '18px',
  color: '#555',
  transition: 'color 0.2s ease'
};

const noMembersStyle = {
  textAlign: 'center',
  color: '#666'
};

export default MembersMap;